import { type ForwardedRef, type Key, type ReactNode, forwardRef, useMemo, useEffect, useRef } from 'react';
import { useHover } from 'react-aria';
import type { GridListProps } from 'react-aria-components';
import { DropIndicator, GridList, GridListItem, Text, useDragAndDrop } from 'react-aria-components';
import type { Path } from 'react-router';
import type { ProjectContext } from '@gonfalon/app-context/projectContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { isSandbox } from '@gonfalon/constants/constants';
import { concurrentSdkKeysFe } from '@gonfalon/dogfood-flags/dogfood-flags';
import { copyToClipboard } from '@gonfalon/dom/copyToClipboard';
import { useScrollIntoViewOnMount } from '@gonfalon/dom/useScrollIntoViewOnMount';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import { EnvironmentMarker } from '@gonfalon/environments/EnvironmentMarker';
import { MarketplaceEnvironmentIndicator } from '@gonfalon/environments/MarketplaceEnvironmentIndicator/MarketplaceEnvironmentIndicator';
import type { InternalEnvironment } from '@gonfalon/environments/types';
import { toEditProjectEnvironment, toHref, toSdkKeys } from '@gonfalon/navigator/navigators';
import { useUpdateFollowPreferences } from '@gonfalon/rest-api/hooks/useUpdateFollowPreferences';
import type { JSONPatch } from '@gonfalon/rest-api/types';
import { trackSdkKeyCopied } from '@gonfalon/sdk-keys/analytics';
import { useThemeValue } from '@gonfalon/theme/useThemeValue';
import {
  Header,
  IconButton,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  snackbarQueue,
  toastQueue,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';
import nullthrows from 'nullthrows';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { trackEnvironmentEvent } from '../analytics';
import { useFavoriteEnvironmentIds } from '../EnvironmentController/useFavoriteEnvironmentIds';
import { EnvironmentFilters } from '../EnvironmentFilters';
import { type SelectedEnvironment } from '../types';
import { useEventLocation } from '../utils/getEventLocation';

import { computeRingColor } from './computeRingColor';
import { ReplaceEnvironmentMenuTrigger } from './ReplaceEnvironmentMenuTrigger';

import styles from './EnvironmentSelector.module.css';

const mimetype = 'custom/launchdarkly';
const dragItemDataSchema = z.object({ id: z.string(), name: z.string(), color: z.string() });

function parseDragItemData(data: string) {
  return dragItemDataSchema.parse(JSON.parse(data));
}

const configurationActionSchema = z.enum(['copy-key', 'copy-sdk', 'copy-mobile', 'copy-client-side', 'favorite']);

function isPredefinedConfigurationAction(action: unknown): action is z.infer<typeof configurationActionSchema> {
  return configurationActionSchema.safeParse(action).success;
}

type EnvironmentSelectorProps = GridListProps<SelectedEnvironment> & {
  settingsMenuItems?: { header: string; items: Array<{ label: string; to?: Path; onAction?: () => void }> };
  projectKey: string;
  addButton?: ReactNode;
  excludedIds: Set<string>;
  onReorder: (environmentKey: Key, position: 'before' | 'after', otherKeys: Set<Key>) => void;
  onRemove: (environmentKey: Key) => void;
  onReplace: (sourceEnvironment: Key, targetEnvironment: InternalEnvironment) => void;
};

export function EnvironmentSelector({
  projectKey,
  children,
  selectionMode = 'single',
  settingsMenuItems,
  addButton,
  excludedIds,
  onReorder,
  onRemove,
  onReplace,
  ...props
}: EnvironmentSelectorProps) {
  const { context } = useProjectContext();
  const list = Array.from(props.items || []);

  const { dragAndDropHooks } = useDragAndDrop({
    acceptedDragTypes: ['text/plain', mimetype],
    getItems(keys) {
      return [...keys].map((key) => {
        const item = nullthrows(list.find((env) => env.key === key));
        return {
          'text/plain': item.key,
          [mimetype]: JSON.stringify({ id: item._id, name: item.name, color: item.color }),
        };
      });
    },
    onReorder(event) {
      if (event.target.dropPosition === 'on') {
        return;
      }

      onReorder(event.target.key, event.target.dropPosition, event.keys);
    },
    renderDropIndicator(target) {
      return <DropIndicator target={target} className={styles.dropIndicator} />;
    },
    renderDragPreview([item]) {
      const data = parseDragItemData(item[mimetype]);
      return (
        <div className={styles.dragPreview}>
          <EnvironmentMarker color={data.color} />
          {data.name}
        </div>
      );
    },
  });

  const { ref } = useScrollIntoViewOnMount<HTMLDivElement>();
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selector = selectorRef.current;
    if (!selector) {
      return;
    }

    // The environment selector scrolls independently of the layout's main scroller, so we
    // mirror its scroll state onto the sticky context to drive the controls edge fade.
    const contextEl = selector.closest('[data-context]');

    const handleScroll = () => {
      const { scrollLeft, clientWidth, scrollWidth } = selector;
      const isScrolled = scrollLeft > 0;
      const isScrolledRight = scrollLeft + clientWidth < scrollWidth - 1;

      selector.toggleAttribute('data-scrolled', isScrolled);
      selector.toggleAttribute('data-scrolled-right', isScrolledRight);

      if (contextEl) {
        contextEl.toggleAttribute('data-scrolled', isScrolled);
        contextEl.toggleAttribute('data-scrolled-right', isScrolledRight);
      }
    };

    selector.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => selector.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={selectorRef} className={styles.environmentSelector} data-selection-mode={selectionMode}>
      <GridList
        {...props}
        layout="grid"
        className={styles.list}
        aria-label="Selected environments"
        dragAndDropHooks={dragAndDropHooks}
        selectionMode={selectionMode === 'single' ? 'single' : 'multiple'}
      >
        {(item) => (
          <GridListItem textValue={item.name} id={item.key} key={item.key} className={styles.item}>
            {({ isSelected }) => (
              <EnvironmentSelectorItem
                ref={ref}
                item={item}
                excludedIds={excludedIds}
                context={context}
                isSelected={isSelected}
                settingsMenuItems={settingsMenuItems}
                onRemove={list.length >= 2 ? onRemove : undefined}
                onReplace={onReplace}
              />
            )}
          </GridListItem>
        )}
      </GridList>
      {addButton ? <div className={styles.add}>{addButton}</div> : null}
    </div>
  );
}

type EnvironmentSelectorItemProps = {
  context: ProjectContext;
  item: SelectedEnvironment;
  excludedIds: Set<string>;
  isSelected: boolean;
  settingsMenuItems?: { header: string; items: Array<{ label: string; to?: Path; onAction?: () => void }> };
  onRemove?: (environmentKey: Key) => void;
  onReplace: (sourceEnvironment: Key, targetEnvironment: InternalEnvironment) => void;
};

const EnvironmentSelectorItem = forwardRef(
  (
    { item, excludedIds, context, isSelected, settingsMenuItems, onRemove, onReplace }: EnvironmentSelectorItemProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const favorites = useFavoriteEnvironmentIds();
    const isFavorite = favorites.includes(item._id);
    const { ref: itemNameRef, hasOverflow, props: overflowProps } = useTextOverflow<HTMLDivElement>();
    const { hoverProps, isHovered } = useHover({});
    const location = useEventLocation();

    const theme = useThemeValue();
    const ringColor = useMemo(() => computeRingColor(`#${item.color}`, theme), [item.color, theme]);

    const { mutate } = useUpdateFollowPreferences();

    const updateFavorite = (instructions: JSONPatch) =>
      mutate(
        { body: instructions },
        {
          onSuccess() {
            toastQueue.add({ title: isFavorite ? 'Removed favorite' : 'Added favorite', status: 'success' });
          },
          onError() {
            snackbarQueue.add({ description: 'Failed to update tags', status: 'error' });
          },
        },
      );

    return (
      <div
        ref={isSelected ? ref : undefined}
        className={styles.environment}
        style={{
          // @ts-expect-error --environment-ring-color is not a known css property
          '--environment-ring-color': ringColor,
        }}
      >
        {/* @ts-expect-error RAC adds an aria label */}
        <IconButton slot="drag" variant="minimal" icon="grip-horiz" size="small" className={styles.drag} />
        <EnvironmentMarker className={styles.marker} color={item.color} />
        <div className={styles.nameGroup}>
          <TooltipTrigger isDisabled={!hasOverflow} isOpen={hasOverflow && isHovered}>
            <div
              ref={itemNameRef}
              data-text={item.name}
              {...overflowProps}
              {...hoverProps}
              className={clsx(styles.name, overflowProps.className)}
            >
              {item.name}
            </div>
            <Tooltip triggerRef={itemNameRef} placement="bottom start" className={styles.overflowName}>
              {item.name}
            </Tooltip>
          </TooltipTrigger>
          <MarketplaceEnvironmentIndicator envKey={item.key} projectKey={context.projectKey} />
        </div>

        <TooltipTrigger>
          <ReplaceEnvironmentMenuTrigger
            className={styles.swap}
            projectKey={context.projectKey}
            excludedIds={new Set([item._id, ...Array.from(excludedIds)])}
            onSelect={(env) => {
              onReplace(item.key, env);
            }}
          />
          <Tooltip placement="bottom">Swap environment</Tooltip>
        </TooltipTrigger>

        <MenuTrigger
          onOpenChange={(open) => {
            if (open) {
              trackEnvironmentEvent('More Environment Actions Clicked', { environmentKey: item.key, location });
            }
          }}
        >
          <TooltipTrigger>
            <IconButton
              aria-label="Environment configuration"
              variant="minimal"
              icon="more-vert"
              size="small"
              className={styles.actions}
              isDisabled={isSandbox()}
            />
            <Tooltip placement="bottom">More environment actions</Tooltip>
          </TooltipTrigger>
          <Popover placement="bottom end">
            <Menu
              onAction={async (action) => {
                if (!isPredefinedConfigurationAction(action)) {
                  return;
                }

                switch (action) {
                  case 'favorite':
                    if (isFavorite) {
                      const found: number[] = [];
                      for (const [index, id] of favorites.entries()) {
                        if (id === item._id) {
                          found.push(index);
                        }
                      }

                      if (found.length === 0) {
                        return;
                      }

                      const instructions = [];
                      for (const index of found) {
                        instructions.push({
                          op: 'remove',
                          path: `/resources/${index}`,
                        } as const);
                      }

                      updateFavorite(instructions);
                    } else {
                      if (favorites.findIndex((value) => item._id === value) !== -1) {
                        return;
                      }

                      updateFavorite([
                        {
                          op: 'add',
                          path: '/resources/-',
                          value: { id: item._id, resourceKind: 'environment' },
                        },
                      ]);
                    }
                    break;
                  default:
                    trackCopyAction(action);
                    await copyKey(action, item);
                    break;
                }
              }}
            >
              <MenuSection>
                <Header>Copy…</Header>
                <MenuItem id="copy-key">
                  <Text slot="label">Environment key</Text>
                </MenuItem>

                {!concurrentSdkKeysFe() && (
                  <>
                    <MenuItem id="copy-sdk">
                      <Text slot="label">SDK key</Text>
                    </MenuItem>
                    <MenuItem id="copy-mobile">
                      <Text slot="label">Mobile key</Text>
                    </MenuItem>
                    <MenuItem id="copy-client-side">
                      <Text slot="label">Client-side ID</Text>
                    </MenuItem>
                  </>
                )}
              </MenuSection>

              {concurrentSdkKeysFe() && (
                <MenuSection>
                  <MenuItem
                    id="to-sdk-keys"
                    href={toHref(
                      toSdkKeys({
                        search: new URLSearchParams({ projKey: context.projectKey, envKey: item.key }),
                      }),
                    )}
                  >
                    <Text slot="label">
                      <Icon name="key" size="small" />
                      View SDK keys
                    </Text>
                  </MenuItem>
                </MenuSection>
              )}

              <MenuSection>
                <MenuItem id="favorite" className={styles.favoriteItem} data-favorite={isFavorite ? true : undefined}>
                  <Text slot="label">
                    {isFavorite ? (
                      <>
                        <Icon name="star" size="small" />
                        Remove from favorites
                      </>
                    ) : (
                      <>
                        <Icon name="star-outline" size="small" />
                        Add to favorites
                      </>
                    )}
                  </Text>
                </MenuItem>
              </MenuSection>
              <MenuSection>
                <MenuItem
                  id="edit-environment-entity"
                  href={toHref(
                    toEditProjectEnvironment({
                      projectKey: context.projectKey,
                      environmentKey: item.key,
                    }),
                  )}
                >
                  <Text slot="label">Edit environment</Text>
                </MenuItem>
              </MenuSection>
              {settingsMenuItems !== undefined ? (
                <MenuSection>
                  <Header>{settingsMenuItems.header}</Header>
                  {settingsMenuItems.items.map((menuItem) => (
                    <MenuItem
                      key={menuItem.label}
                      id={menuItem.label}
                      href={menuItem.to ? toHref(menuItem.to) : undefined}
                      onAction={menuItem.onAction}
                    >
                      <Text slot="label">{menuItem.label}</Text>
                    </MenuItem>
                  ))}
                </MenuSection>
              ) : null}
            </Menu>
          </Popover>
        </MenuTrigger>
        <EnvironmentFilters environment={item} />
        {onRemove !== undefined ? (
          <TooltipTrigger>
            <IconButton
              aria-label="remove environment"
              icon="cancel"
              size="small"
              variant="minimal"
              className={styles.remove}
              onPress={() => {
                trackEnvironmentEvent('Close Environment Clicked', { environmentKey: item.key, location });
                onRemove(item.key);
              }}
            />
            <Tooltip placement="bottom">Close environment</Tooltip>
          </TooltipTrigger>
        ) : null}
      </div>
    );
  },
);

async function copyKey(action: z.infer<typeof configurationActionSchema>, item: SelectedEnvironment) {
  if (action === 'copy-key') {
    await copyToClipboard(item.key, 'Environment key copied to clipboard');
    return;
  }

  let key: string;
  if (action === 'copy-client-side') {
    key = item._id;
  } else if (action === 'copy-mobile') {
    key = item.mobileKey;
  } else if (action === 'copy-sdk') {
    key = item.apiKey;
  } else {
    return;
  }

  await copyToClipboard(key, 'SDK key copied to clipboard');
}

function trackCopyAction(action: z.infer<typeof configurationActionSchema>) {
  switch (action) {
    case 'copy-key':
      trackSdkKeyCopied({ source: 'Environment Key' });
      break;
    case 'copy-sdk':
      trackSdkKeyCopied({ source: 'SDK Key' });
      break;
    case 'copy-mobile':
      trackSdkKeyCopied({ source: 'Mobile Key' });
      break;
    case 'copy-client-side':
      trackSdkKeyCopied({ source: 'Client-side ID' });
      break;
    default:
      break;
  }
}
